"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import Link from "next/link"
import {
  Check,
  Mail,
  Minus,
  Plus,
  ShoppingBag,
  Zap,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { TextMorph } from "@/components/ui/text-morph"
import { useCart } from "@/lib/cart-store"

function formatEuros(n: number) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(n)
}

export type ProductCartActionsProps = {
  id: string
  slug: string
  name: string
  brand?: string
  image?: string
  /** HT price in €. When absent, the CTA switches to "devis" (contact sales). */
  unitPrice?: number | null
}

/**
 * CTA block for /produit/[slug] — adds a product to the localStorage cart.
 *
 * Behaviour:
 *   • price set      → quantity picker + "Ajouter au panier" + "Acheter maintenant"
 *   • price missing  → "Nous contacter pour devis" (links to /contact)
 *
 * "Acheter maintenant" is a Buy-Now convenience: it clears any previous
 * items of this product from the cart, adds the chosen quantity, then
 * navigates to /panier/commander. The checkout API (/api/checkout)
 * validates the cart server-side and creates a Stripe PaymentIntent when
 * STRIPE_SECRET_KEY is set.
 */
export function ProductCartActions(props: ProductCartActionsProps) {
  const { id, slug, name, brand, image, unitPrice } = props
  const router = useRouter()
  const { add, items } = useCart()
  const [qty, setQty] = useState(1)
  const [justAdded, setJustAdded] = useState(false)

  const alreadyInCart = items.find((i) => i.id === id)

  const hasPrice = typeof unitPrice === "number" && unitPrice > 0

  if (!hasPrice) {
    return (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Button
          asChild
          size="lg"
          className="group h-12 gap-3 rounded-full pl-6 pr-2 active:scale-[0.97]"
        >
          <Link href={`/contact?product=${encodeURIComponent(slug)}`}>
            <Mail className="size-4" />
            <span>Nous contacter</span>
            <span className="flex size-8 items-center justify-center rounded-full bg-primary-foreground/20 transition-transform duration-200 group-hover:translate-x-0.5">
              →
            </span>
          </Link>
        </Button>
        <p className="text-sm text-muted-foreground">
          Prix sur demande — réponse sous 24 h ouvrées.
        </p>
      </div>
    )
  }

  function addToCart(quantityToAdd: number) {
    add({
      id,
      slug,
      name,
      brand,
      image,
      unitPrice: unitPrice!,
      quantity: quantityToAdd,
    })
  }

  function handleAddToCart() {
    addToCart(qty)
    setJustAdded(true)
    window.setTimeout(() => setJustAdded(false), 2200)
  }

  function handleBuyNow() {
    addToCart(qty)
    // Go straight to the checkout step rather than the cart review —
    // /panier/commander is the server-guarded page that bounces
    // unauthenticated visitors through /connexion?redirect=/panier/
    // commander, so after login they land back on the payment step
    // with the product they just clicked still in cart.
    router.push("/panier/commander")
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-baseline gap-3">
        <span className="text-3xl font-bold tracking-tight">
          {formatEuros(unitPrice!)}
        </span>
        <span className="text-sm text-muted-foreground">HT</span>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-9 rounded-full"
            onClick={() => setQty((q) => Math.max(1, q - 1))}
            aria-label="Réduire la quantité"
          >
            <Minus className="size-4" />
          </Button>
          <span
            className="w-8 text-center text-sm font-medium tabular-nums"
            aria-live="polite"
          >
            {qty}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-9 rounded-full"
            onClick={() => setQty((q) => q + 1)}
            aria-label="Augmenter la quantité"
          >
            <Plus className="size-4" />
          </Button>
        </div>

        <Button
          type="button"
          size="lg"
          variant="outline"
          className="group h-12 gap-2 rounded-full active:scale-[0.97]"
          onClick={handleAddToCart}
        >
          {justAdded ? (
            <Check className="size-4" />
          ) : (
            <ShoppingBag className="size-4" />
          )}
          <TextMorph as="span" duration={400}>
            {justAdded ? "Ajouté au panier" : "Ajouter au panier"}
          </TextMorph>
        </Button>

        <Button
          type="button"
          size="lg"
          className="group h-12 gap-2 rounded-full active:scale-[0.97]"
          onClick={handleBuyNow}
        >
          <Zap className="size-4" />
          Acheter maintenant
        </Button>
      </div>

      {alreadyInCart && alreadyInCart.quantity > 0 ? (
        <p className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Check className="size-3 text-primary" />
          {alreadyInCart.quantity} déjà dans votre panier —{" "}
          <Link
            href="/panier"
            className="underline underline-offset-2 hover:text-foreground"
          >
            voir le panier
          </Link>
        </p>
      ) : null}

      {/*
       * Trust strip — answers the four B2B objections inline so the
       * buyer doesn't have to scroll to /maintenance or /contact to
       * confirm them. Pulled from the CRO audit's "five questions a
       * pro asks before buying": price (above), fit (specs above),
       * delivery (here), SAV (here), advice (here).
       */}
      <ul className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-2xl border border-border bg-card/60 p-3 text-xs text-muted-foreground sm:grid-cols-4">
        <li className="flex items-start gap-1.5">
          <Check className="mt-0.5 size-3 shrink-0 text-primary" />
          <span>Livraison 24–48 h Île-de-France</span>
        </li>
        <li className="flex items-start gap-1.5">
          <Check className="mt-0.5 size-3 shrink-0 text-primary" />
          <span>SAV 48–72 h ouvrées</span>
        </li>
        <li className="flex items-start gap-1.5">
          <Check className="mt-0.5 size-3 shrink-0 text-primary" />
          <span>
            Conseil comptoir{" "}
            <a
              href="tel:+33130370530"
              className="tabular-nums text-foreground underline underline-offset-2 hover:text-primary"
            >
              01 30 37 05 30
            </a>
          </span>
        </li>
        <li className="flex items-start gap-1.5">
          <Check className="mt-0.5 size-3 shrink-0 text-primary" />
          <span>Paiement sécurisé Stripe</span>
        </li>
      </ul>

      {/*
       * Two low-friction "talk to a human" escape hatches. The first
       * is the existing devis/conseil link. The second is a B2B-
       * specific cross-sell hint — pros routinely worry "fits my
       * machine?" and a clear path to the comptoir to verify
       * compatibility removes the friction that otherwise sends them
       * back to the search bar.
       */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <Link
          href={`/contact?product=${encodeURIComponent(slug)}`}
          className="inline-flex items-center gap-1.5 hover:text-foreground"
        >
          <Mail className="size-3.5" aria-hidden="true" />
          Nous contacter
        </Link>
        <span aria-hidden="true">·</span>
        <Link
          href={`/contact?product=${encodeURIComponent(slug)}&topic=compatibility`}
          className="inline-flex items-center gap-1.5 hover:text-foreground"
        >
          Compatible avec votre machine ? Demander
        </Link>
      </div>
    </div>
  )
}
